<template>
  <div>
    <v-btn
      v-if="!show"
      v-on:click="show = true"
      class="px-0 mx-0 mb-0 grey--text text--darken-2 mono"
      elevation="0"
      color="white">
      Profil-Codes <v-icon>mdi-chevron-down</v-icon>
    </v-btn>
    <v-btn
      v-if="show"
      v-on:click="show = false"
      class="px-0 mx-0 mb-0 grey--text text--darken-2 mono"
      elevation="0"
      color="white">
      Profil-Codes <v-icon>mdi-chevron-up</v-icon>
    </v-btn>

    <div v-show="show" class="mb-0 pt-3">
      <p v-if="target.profileCodes.length === 0" class="grey--text text--darken-2 small">
        Noch kein Profil-Code vorhanden.
      </p>

      <p v-for="item in target.profileCodes" :key="item.id" class="grey--text text--darken-2 small">
        <span v-if="editable">
          <v-btn
            icon
            x-small
            :disabled="item.isCanonical || switchingCanonical"
            :aria-label="item.isCanonical ? `Kanonischer Profil-Code ${item.code}` : `Profil-Code ${item.code} als kanonisch festlegen`"
            :aria-pressed="item.isCanonical ? 'true' : 'false'"
            @click="setCanonical(item)"
          >
            <v-icon small :color="item.isCanonical ? 'green' : 'grey'">
              {{ item.isCanonical ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
          </v-btn>
        </span>
        <span class="mono mb-0">{{ item.code }}</span>
        <span v-if="item.isCanonical"> (kanonisch)</span>
        <span v-if="editable && !item.isCanonical">
          <v-btn
            icon
            x-small
            :aria-label="`Profil-Code ${item.code} löschen`"
            @click="requestDelete(item)"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </span>
        <span v-if="error && error.item === item" class="red--text"> {{ error.message }}</span>
      </p>

      <div v-if="editable" class="pt-2">
        <v-text-field
          v-model="newCode"
          label="Neuer Code (optional, 6 Zeichen)"
          dense
          style="max-width: 220px; display:inline-block;"
        ></v-text-field>
        <v-btn small text color="primary" :loading="adding" @click="addCode">
          Hinzufügen
        </v-btn>
        <p v-if="addError" class="red--text small">{{ addError }}</p>
      </div>
    </div>

    <AdminConfirmDialog
      v-model="dialogDelete"
      title="Profil-Code löschen?"
      confirm-label="Löschen"
      cancel-label="Abbrechen"
      busy-label="Löschen..."
      confirm-variant="danger"
      :busy="deleting"
      @confirm="deleteConfirmed"
      @cancel="cancelDelete"
    >
      Dieser Code wird dann nicht mehr auffindbar sein. Bereits verteilte URLs mit diesem Code
      funktionieren danach nicht mehr. Der Code bleibt dauerhaft reserviert und kann nicht erneut
      vergeben werden – auch nicht an diesen Eintrag.
      <p v-if="deleteError" class="red--text small mt-2">{{ deleteError }}</p>
    </AdminConfirmDialog>
  </div>
</template>

<script>
import AdminConfirmDialog from './AdminConfirmDialog'

/**
 * Lists, adds, and removes ProfileCodes (short public facade-URL codes) of an
 * Account/Contact. Rendered below the "Lieferanten" section in Account.vue /
 * Contact.vue. Editable only for admins, on non-readonly, non-merged entities.
 */
export default {
  name: 'ProfileCodes',
  components: {
    AdminConfirmDialog
  },
  props: {
    target: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    show: false,
    newCode: '',
    adding: false,
    addError: null,
    dialogDelete: false,
    itemForDeletion: null,
    deleting: false,
    deleteError: null,
    switchingCanonical: false,
    error: null
  }),
  computed: {
    editable () {
      if (this.readonly === true) {
        return false
      }
      if (this.target.trueReferenceId) {
        // merged - manage codes on the canonical target instead.
        return false
      }
      // ROLE_DATA_ADMIN implies ROLE_ADMIN backend-side (security.yaml role
      // hierarchy), but $auth.isAdmin() only checks the literal ROLE_ADMIN
      // string - check the hierarchy here too, or a pure ROLE_DATA_ADMIN user
      // would see the section but never be able to use it.
      return this.$auth.isAdmin() || (this.$auth.user && this.$auth.user.roles.includes('ROLE_DATA_ADMIN'))
    }
  },
  methods: {
    async addCode () {
      this.addError = null
      this.adding = true
      try {
        const response = await this.$http.post('/profile-code', {
          targetEntityType: this.target.class && this.target.class.includes('Contact') ? 'contact' : 'account',
          targetEntityId: this.target.id,
          code: this.newCode || null
        })
        this.target.profileCodes.push(response.data)
        if (response.data.isCanonical) {
          this.target.canonicalProfileCode = response.data.code
        }
        this.newCode = ''
      } catch (exception) {
        this.addError = exception.response && exception.response.data && exception.response.data.message
          ? exception.response.data.message
          : 'Code konnte nicht hinzugefügt werden.'
      } finally {
        this.adding = false
      }
    },
    async setCanonical (item) {
      if (!this.editable || item.isCanonical || this.switchingCanonical) {
        return
      }
      this.error = null
      this.switchingCanonical = true
      try {
        await this.$http.put('/profile-code/' + item.id + '/canonical')
        this.target.profileCodes.forEach(code => {
          code.isCanonical = (code.id === item.id)
        })
        this.target.canonicalProfileCode = item.code
      } catch (exception) {
        this.error = {
          item,
          message: exception.response && exception.response.data && exception.response.data.message
            ? exception.response.data.message
            : 'Konnte nicht als kanonisch gesetzt werden.'
        }
      } finally {
        this.switchingCanonical = false
      }
    },
    requestDelete (item) {
      this.itemForDeletion = item
      this.deleteError = null
      this.dialogDelete = true
    },
    cancelDelete () {
      this.dialogDelete = false
      this.itemForDeletion = null
      this.deleteError = null
    },
    async deleteConfirmed () {
      this.deleting = true
      this.deleteError = null
      try {
        await this.$http.delete('/profile-code/' + this.itemForDeletion.id)
        this.target.profileCodes = this.target.profileCodes.filter(code => code.id !== this.itemForDeletion.id)
        this.dialogDelete = false
        this.itemForDeletion = null
      } catch (exception) {
        this.deleteError = exception.response && exception.response.data && exception.response.data.message
          ? exception.response.data.message
          : 'Code konnte nicht gelöscht werden.'
        // Keep the dialog open on error so the admin sees why it failed,
        // instead of silently closing as if the delete had succeeded.
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
